import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import Logo from 'design-system/Logo';
import Text from 'design-system/Text';
import CardRow from 'design-system/CardRow';
import Squircle from 'design-system/Squircle';
import BottomPanel from 'design-system/BottomPanel';
import { IconCoins } from 'design-system/icons/IconCoins';
import { IconRocket } from 'design-system/icons/IconRocket';
import { LineSeparator } from 'design-system/LineSeparator';
import CardRowContainer from 'design-system/CardRowContainer';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { AppView, Button, CardView, NavigationHeader, Spacer } from 'design-system';
import { hitSlopS, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { formatAmount, getOrdinal } from 'utils/formatting';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useFetchOld } from 'hooks';
import Amplitude from 'utils/amplitude';
import IconTooltip from 'design-system/icons/IconTooltip';
import Modal from 'utils/packages/Modal';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { OnboardingFlow } from 'utils/types/navigation';
import { getDirectDebitEstimatedDate } from '../actions';
import useStockMeta from '../hooks/useStockMeta';
import { useOnboardingState } from '../hooks/selectors';
import { selectAutoInvestFee } from '../reducers/selectors';
import { useCreateInvestAccount } from '../hooks/useCreateInvestAccount';
import AutoInvestFeeInfoModal from '../components/AutoInvestFeeInfoModal';
import InitialInvestmentFeesModal from '../components/InitialInvestmentFeesModal';
import { formatDateInProgressTimeline } from '../utils';
const InvestReviewAndConfirmScreen = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const onboardingState = useOnboardingState();
    const currentAutoInvestFee = useAppSelector(selectAutoInvestFee);
    const stockMeta = useStockMeta(onboardingState?.assetId ?? '');
    const scrollY = useSharedValue(0);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('Review Direct Debit Screen', { source: 'invest' });
        }
    }, [mountedOnAnotherScreen]);
    const { submit } = useCreateInvestAccount();
    const [directDebitEstimatedDate, , fetching, fetchDirectDebitEstimatedDate] = useFetchOld();
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    const accountName = onboardingState?.fundingAccount &&
        'name' in onboardingState.fundingAccount &&
        onboardingState?.fundingAccount.bankName &&
        onboardingState?.fundingAccount.name
        ? `${onboardingState?.fundingAccount.bankName} · ${onboardingState?.fundingAccount.name}`
        : onboardingState?.fundingAccount?.bankName || 'Linked bank';
    const initialAmount = onboardingState?.amount?.initial;
    const monthlyAmount = onboardingState?.amount?.monthly;
    const autoinvestDayOfMonth = onboardingState?.autoinvestDayOfMonth;
    useEffect(() => {
        if (initialAmount) {
            fetchDirectDebitEstimatedDate(getDirectDebitEstimatedDate());
        }
    }, [initialAmount, fetchDirectDebitEstimatedDate]);
    const estimatedCollectionDate = useMemo(() => {
        if (!directDebitEstimatedDate?.estimatedCollectionDate)
            return '';
        return formatDateInProgressTimeline(directDebitEstimatedDate.estimatedCollectionDate);
    }, [directDebitEstimatedDate]);
    const collectionDateString = useMemo(() => (autoinvestDayOfMonth ? `${getOrdinal(autoinvestDayOfMonth)} of each month` : ''), [autoinvestDayOfMonth]);
    const handleConfirm = useCallback(() => {
        submit();
    }, [submit]);
    const showFeeModal = useCallback(() => {
        if (typeof currentAutoInvestFee === 'number') {
            AutoInvestFeeInfoModal.show({ value: currentAutoInvestFee, currency: 'GBP' }, true);
        }
    }, [currentAutoInvestFee]);
    const showEstimatedCollectionDateModal = useCallback(() => {
        Modal.showConfirmation({
            title: 'Estimated collection date',
            subtitle: 'We’re setting up a Direct Debit to collect your initial investment. This can take 3–5 working days to complete. Once it does, we’ll invest the money directly into your chosen fund.',
            confirmButtonTitle: 'Okay',
        });
    }, []);
    const showFeesModal = useCallback(() => {
        InitialInvestmentFeesModal.show();
    }, []);
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} title="" rightContent={<View style={mountedOnAnotherScreen ? styles.headerRight : null}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title="Review and confirm"/>

        <CardView noMarginTop>
          <View style={styles.fundRow}>
            <Logo R44 uri={stockMeta?.iconUrl} resizeMode="contain"/>
            <View style={styles.flex}>
              <Text Text-16 numberOfLines={1}>
                {stockMeta?.name}
              </Text>
              <Text TextThin-14 Secondary numberOfLines={2}>
                {stockMeta?.shortDescription || stockMeta?.description}
              </Text>
            </View>
          </View>
        </CardView>

        {!!initialAmount && (<CardView noMarginTop>
            <View style={styles.cardHeader}>
              <View style={styles.flex}>
                <Text Text-16>Initial investment</Text>
              </View>
              <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                <IconCoins alt width={24} height={24} color={colors.elements.secondary}/>
              </Squircle>
            </View>
            <LineSeparator noMargin/>
            <CardRowContainer>
              <CardRow title={<View style={styles.row}>
                    <Text TextThin-14 Secondary>
                      Estimated collection date
                    </Text>
                    <Spacer w={4}/>
                    <View>
                      <Spacer h={4}/>
                      <TouchableOpacity hitSlop={hitSlopS} onPress={showEstimatedCollectionDateModal}>
                        <IconTooltip height={14} width={14} colorKey="secondary"/>
                      </TouchableOpacity>
                    </View>
                  </View>} info={estimatedCollectionDate} small smallPadding requesting={fetching}/>
              <CardRow title="Account" info={accountName} small smallPadding/>
              <CardRow title={<View style={styles.row}>
                    <Text TextThin-14 Secondary>
                      Fees
                    </Text>
                    <Spacer w={4}/>
                    <View>
                      <Spacer h={4}/>
                      <TouchableOpacity hitSlop={hitSlopS} onPress={showFeesModal}>
                        <IconTooltip height={14} width={14} colorKey="secondary"/>
                      </TouchableOpacity>
                    </View>
                  </View>} info={<Text Numbers-14>{formatAmount(0, 'GBP', 2)}</Text>} small smallPadding/>
              <CardRow title="Amount" info={<Text Numbers-14>{formatAmount(initialAmount, 'GBP', 2)}</Text>} small smallPadding/>
            </CardRowContainer>
          </CardView>)}

        {!!monthlyAmount && (<CardView noMarginTop>
            <View style={styles.cardHeader}>
              <View style={styles.flex}>
                <Text Text-16>Autoinvest</Text>
              </View>
              <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                <IconRocket color={colors.text.investment}/>
              </Squircle>
            </View>
            <LineSeparator noMargin/>
            <CardRowContainer>
              <CardRow title="Collection date" info={collectionDateString} small smallPadding/>
              <CardRow title="Account" info={accountName} small smallPadding/>
              {typeof currentAutoInvestFee === 'number' && (<CardRow title={<View style={styles.row}>
                      <Text TextThin-14 Secondary>
                        Autoinvest fee
                      </Text>
                      <Spacer w={4}/>
                      <View>
                        <Spacer h={4}/>
                        <TouchableOpacity hitSlop={hitSlopS} onPress={showFeeModal}>
                          <IconTooltip height={14} width={14} colorKey="secondary"/>
                        </TouchableOpacity>
                      </View>
                    </View>} info={<Text Numbers-14>{formatAmount(currentAutoInvestFee ?? 0, 'GBP', 2)}</Text>} small smallPadding/>)}
              <CardRow title="Amount" info={<Text Numbers-14>{formatAmount(monthlyAmount, 'GBP', 2)}</Text>} small smallPadding/>
            </CardRowContainer>
          </CardView>)}
      </Animated.ScrollView>
      <BottomPanel isInTab={!!mountedOnAnotherScreen}>
        <Text TextThin-16 Secondary centered>
          Capital at risk
        </Text>
        <Spacer h={16}/>
        <Button brand title="Confirm" onPress={handleConfirm}/>
      </BottomPanel>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    fundRow: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
        gap: rem(16),
    },
    flex: {
        flex: 1,
    },
    cardHeader: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    headerRight: {
        alignItems: 'flex-end',
        paddingRight: rem(16),
    },
    row: {
        flexDirection: 'row',
    },
}));
export default InvestReviewAndConfirmScreen;
